/**
 * useStandardElement Hook
 * 
 * A React hook that provides a standardized interface for working with elements.
 * This hook automatically handles event schema registration, lifecycle events,
 * and provides a simplified event broadcasting interface.
 */

import { useEffect } from 'react';
import { ElementSchema } from '../schemas/base-element-schemas';
import { useElementEvents } from './useElementEvents';

/**
 * Options for the useStandardElement hook
 */
export interface UseStandardElementOptions {
  /**
   * Whether to automatically broadcast mounted/unmounted events
   * @default true
   */
  autoLifecycleEvents?: boolean;
  
  /**
   * Additional data to include in lifecycle events
   */
  lifecycleEventData?: Record<string, unknown>;
}

/**
 * Hook for working with standardized elements
 * 
 * @param schema - The element schema object containing type, name, and events
 * @param elementId - The unique ID of the element instance
 * @param options - Additional options for element behavior
 * @returns An object with the broadcast function and element metadata
 */
export function useElement(
  schema: ElementSchema,
  elementId: string,
  options: UseStandardElementOptions = {}
) {
  const { ELEMENT_TYPE, ELEMENT_NAME, eventSchemas, getEventName } = schema;
  const { autoLifecycleEvents = true, lifecycleEventData = {} } = options;
  
  // Use the simpler hook for event registration and broadcasting
  const elementEvents = useElementEvents(
    ELEMENT_TYPE,
    elementId,
    eventSchemas,
    getEventName // This is now optional
  );
  
  // Handle automatic lifecycle events if enabled
  useEffect(() => {
    if (autoLifecycleEvents) {
      // Broadcast the mounted event
      elementEvents.broadcast('mounted', {
        timestamp: new Date().toISOString(),
        elementId,
        ...lifecycleEventData
      });
      
      // Return cleanup function to broadcast unmounted event
      return () => {
        elementEvents.broadcast('unmounted', {
          timestamp: new Date().toISOString(),
          elementId,
          ...lifecycleEventData
        });
      };
    }
    
    return undefined;
  }, [autoLifecycleEvents, elementEvents, elementId, lifecycleEventData]);
  
  // Helper for broadcasting error events
  const broadcastError = (errorMessage: string, errorCode?: string) => {
    elementEvents.broadcast('error', {
      errorMessage,
      errorCode,
      timestamp: new Date().toISOString(),
      elementId
    });
  };
  
  // Helper for broadcasting ready event
  const broadcastReady = () => {
    elementEvents.broadcast('ready', {
      timestamp: new Date().toISOString(),
      elementId
    });
  };
  
  // Helper for broadcasting state changes
  const broadcastStateChange = (currentState: string, previousState?: string) => {
    elementEvents.broadcast('stateChanged', {
      currentState,
      previousState,
      timestamp: new Date().toISOString(),
      elementId
    });
  };
  
  return {
    // Core properties
    elementId,
    elementType: ELEMENT_TYPE,
    elementName: ELEMENT_NAME,
    
    // Event broadcasting
    events: elementEvents,
    
    // Helper methods for standard events
    broadcastError,
    broadcastReady,
    broadcastStateChange
  };
}
